import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Tabs, Tab, makeStyles } from '@material-ui/core';
import { Android as BotIcon, DragIndicator as DragIcon } from '@material-ui/icons';
import { Quiz as PromptsIcon } from '@mui/icons-material';
import { Agent, Message } from './types';
import { AgentsTab } from './AgentsTab';
import { PromptsTab } from './PromptsTab';

const useStyles = makeStyles(theme => ({
  sidebarContainer: {
    position: 'relative',
    display: 'flex',
    minWidth: 300,
    maxWidth: 460,
    height: '100%',
  },
  sidebar: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(10px)',
    overflow: 'hidden',
  },
  resizeHandle: {
    position: 'absolute',
    top: '1%',
    left: 0,
    width: 11,
    height: '98%',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  dragIcon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  tabPanel: {
    padding: theme.spacing(2),
    height: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[300],
      borderRadius: '2px',
      '&:hover': { background: theme.palette.grey[400] },
    },
  },
}));

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  const classes = useStyles();
  return (
    <div hidden={value !== index}>
      {value === index && <Box className={classes.tabPanel}>{children}</Box>}
    </div>
  );
};

interface SidebarProps {
  agents: Agent[];
  selectedAgent: string;
  onAgentSelect: (agentName: string) => void;
  messages: Message[];
  onPromptSelect?: (value: string) => void;
  tabValue?: number;
  onTabChange?: (value: number) => void;
  basePath?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  agents,
  selectedAgent,
  onAgentSelect,
  onPromptSelect,
  tabValue: externalTabValue,
  onTabChange,
  basePath,
}) => {
  const classes = useStyles();
  const [internalTabValue, setInternalTabValue] = React.useState<number>(0);
  const tabValue = externalTabValue !== undefined ? externalTabValue : internalTabValue;
  const setTabValue = onTabChange || setInternalTabValue;
  const [width, setWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedAgentObj = agents.find(a => a.name === selectedAgent);
  const selectedAgentPrompts = selectedAgentObj?.prompts || {};
  const showPromptsTab = Object.keys(selectedAgentPrompts).length > 0;

  useEffect(() => {
    if (!showPromptsTab && tabValue === 1) setTabValue(0);
  }, [showPromptsTab, tabValue]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const newWidth = rect.right - e.clientX;
    if (newWidth >= 200 && newWidth <= 500) setWidth(newWidth);
  }, [isResizing]);

  const handleMouseUp = useCallback(() => setIsResizing(false), []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div ref={containerRef} className={classes.sidebarContainer} style={{ width }}>
      <Box className={classes.sidebar}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<BotIcon />} label="Agents" />
          {showPromptsTab && <Tab icon={<PromptsIcon />} label="Prompts" />}
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          <AgentsTab agents={agents} selectedAgent={selectedAgent} onAgentSelect={onAgentSelect} basePath={basePath} />
        </TabPanel>
        {showPromptsTab && (
          <TabPanel value={tabValue} index={1}>
            <PromptsTab onPromptSelect={onPromptSelect} prompts={selectedAgentPrompts} />
          </TabPanel>
        )}
      </Box>
      <div className={classes.resizeHandle} onMouseDown={handleMouseDown}>
        <DragIcon className={classes.dragIcon} />
      </div>
    </div>
  );
};
